import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { BUSINESS_INFO, getFullAddress } from '../data/reviewSources';

// DRAFT legal copy written for the website. Have it reviewed by a lawyer
// familiar with India's Digital Personal Data Protection Act 2023 and the
// pet-shop rules that apply to the business before relying on it.
const UPDATED = '13 October 2026';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-main)' }}>{title}</h2>
    <div className="space-y-3 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{children}</div>
  </section>
);

const Shell = ({ title, intro, children }) => (
  <div style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '100vh' }}>
    <SEO />
    <section
      className="py-20"
      style={{ background: 'linear-gradient(to bottom right, #DCFCE7, #FFFFFF)', borderBottom: '1px solid var(--neutral-border)' }}
    >
      <div className="container mx-auto text-center">
        <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>{title}</h1>
        <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--text-muted)' }}>{intro}</p>
      </div>
    </section>
    <section className="py-16" style={{ backgroundColor: 'white' }}>
      <div className="container mx-auto max-w-3xl">
        <p className="mb-8 text-sm" style={{ color: 'var(--text-muted)' }}>Last updated: {UPDATED}</p>
        {children}
        <Section title="Contact">
          <p>
            {BUSINESS_INFO.name}, {getFullAddress()}<br />
            Phone / WhatsApp: <a href={`tel:${BUSINESS_INFO.phone.replace(/\s+/g, '')}`} className="underline">{BUSINESS_INFO.phone}</a>
          </p>
        </Section>
      </div>
    </section>
  </div>
);

export const Privacy = () => (
  <Shell title="Privacy Policy" intro="How Petsmart Puppies handles the information you share with us.">
    <Section title="What we collect">
      <p><strong>Enquiries:</strong> your name, phone number and the details you choose to share when you call us, message us on WhatsApp, use the chat assistant or fill in a form on this site.</p>
      <p><strong>Website usage:</strong> pages visited, device and browser type, approximate location and referring site, collected through Google Analytics and PostHog using cookies or similar technology.</p>
    </Section>
    <Section title="How we use it">
      <p>To answer your enquiry, share information about puppies and products, arrange store visits or deliveries, and improve this website. We do not sell your personal information.</p>
    </Section>
    <Section title="Who we share it with">
      <p>Only with services that help us run the business: WhatsApp (Meta) for messaging, Google (Analytics and Maps), PostHog (website analytics) and the AI provider that powers the chat assistant. Each processes data under its own privacy terms. We may disclose information where the law requires it.</p>
    </Section>
    <Section title="Your choices and rights">
      <p>You can ask us to access, correct or delete your personal information, or withdraw consent, using the contact details below. Under India's Digital Personal Data Protection Act, 2023 you may also approach the Data Protection Board of India.</p>
    </Section>
  </Shell>
);

export const Terms = () => (
  <Shell title="Terms of Use" intro="Terms for using this website and for enquiries made through it.">
    <Section title="Information on this website">
      <p>Breed descriptions, prices and care guides on this site are general information. Availability and prices change; please confirm current details with us before visiting or paying. Our care guides are not veterinary advice; consult a qualified veterinarian about your pet's health.</p>
    </Section>
    <Section title="Puppies">
      <p>Health, vaccination and pedigree details for each puppy, and the terms of any health or breed guarantee, are confirmed in writing at the time of sale. Please ask to see vaccination records and any papers before you buy.</p>
    </Section>
    <Section title="Products and orders">
      <p>Product availability, prices and delivery are confirmed on WhatsApp or by phone before an order is placed.</p>
    </Section>
    <Section title="Third-party links">
      <p>This site links to WhatsApp, Google, Justdial, Instagram, Facebook and YouTube. We are not responsible for their content or practices.</p>
    </Section>
    <Section title="Governing law">
      <p>These terms are governed by the laws of India. Courts in Gautam Buddh Nagar (Noida), Uttar Pradesh have jurisdiction. See also our <Link to="/privacy/" className="underline">Privacy Policy</Link>.</p>
    </Section>
  </Shell>
);

export const NotFound = () => (
  <div style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '60vh' }}>
    <SEO title="Page Not Found | Petsmart Puppies" description="This page could not be found. Browse our puppies, pet supplies and care guides, or contact Petsmart Puppies in Sector 31, Noida." noindex />
    <section className="py-20">
      <div className="container mx-auto text-center">
        <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>Page not found</h1>
        <p className="text-lg max-w-2xl mx-auto mb-8" style={{ color: 'var(--text-muted)' }}>The page you were looking for has moved or no longer exists.</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/" className="underline font-semibold">Home</Link>
          <Link to="/puppies/" className="underline font-semibold">Puppies</Link>
          <Link to="/supplies/" className="underline font-semibold">Pet Supplies</Link>
          <Link to="/blog/" className="underline font-semibold">Blog</Link>
          <Link to="/contact/" className="underline font-semibold">Contact</Link>
        </div>
      </div>
    </section>
  </div>
);
